// ============================================================
// A BODY'S TRAIT LOOKS (plan body-map T1; the bench's trait effects, ruled
// 2026-10-09). What the plate draws of a body's nature, read off its sheet's
// traits and never stored: the psyker's runes by Psy Rating, a force field's
// ring by its protection rating, the daemonic treatment by codex tier. Each
// look owns its own channel and colour, so they stack.
//
// Pure. Zero React dependency.
// ============================================================

import type { Target } from '../../types';
import type { Character } from '../character/types';
import { classifyThreat, TIER_ORDER } from '../threatAssessment';
import { landed, type BurstHit } from './burst';
import type { PlateFigureId } from './figureArt';
import { BACKTRACK } from './natures';

export interface TraitLooks {
  /** The psyker's degree: its Psy Rating, 0-10 (0: not a psyker). */
  psy: number;
  /** The force field's protection rating while it is up (0: none). */
  field: number;
  /** The daemonic treatment's strength by codex tier, 1/7 (MINIMA) to 1 (ULTIMA); 0: not daemonic. */
  daemon: number;
  /** Regeneration (X); 0: none. */
  regen: number;
  /** Toxic (X); 0: not toxic. */
  toxic: number;
  /** Toxic with natural weapons: its fists' veins pulse with it. */
  toxicFists: boolean;
  /** Fear (X); 0: none. */
  fear: number;
  /** The Warp Conduit talent. */
  conduit: boolean;
  /** From Beyond: a window to the void. */
  beyond: boolean;
  incorporeal: boolean;
  phase: boolean;
  /** How it holds itself off the ground: a hoverer, a flyer (a flyer that hovers too flies), or neither. */
  lift: 'hover' | 'fly' | null;
  nightmares: boolean;
  warpInst: boolean;
  undying: boolean;
  /** Natural Armour (X); 0: none. */
  natArmour: number;
  sturdy: boolean;
  /** Black Carapace: the sights lock a size tighter. */
  carapace: boolean;
  /** Deadly Natural Weapons: its fists pulse blood red. */
  dnw: boolean;
  swift: boolean;
  /** Strange Physiology: a lock slides off it. */
  strange: boolean;
  frenzied: boolean;
  /** The Blind trait: it listens; Blinded is refused. */
  blind: boolean;
}

/** A body with no looks at all. */
const NO_LOOKS: TraitLooks = {
  psy: 0,
  field: 0,
  daemon: 0,
  regen: 0,
  toxic: 0,
  toxicFists: false,
  fear: 0,
  conduit: false,
  beyond: false,
  incorporeal: false,
  phase: false,
  lift: null,
  nightmares: false,
  warpInst: false,
  undying: false,
  natArmour: 0,
  sturdy: false,
  carapace: false,
  dnw: false,
  swift: false,
  strange: false,
  frenzied: false,
  blind: false,
};

/** What a dead body shows: none of its looks - only the daemonic treatment, which plays its banishment. */
export const deadLooks = (looks: TraitLooks): TraitLooks => ({ ...NO_LOOKS, daemon: looks.daemon });

/**
 * How long each look's own moment on a landed hit lasts (s): its CSS one-shot's duration (PlateTraits.module.css).
 * Only those that outlast the hit's own play (1.1 s at least) - Natural Armour's 0.9 s flash never does.
 */
const AFTER_HIT = { toxic: 4.4, sturdy: 1.4, backtrack: BACKTRACK.delay + 0.96 };

/**
 * Until when an attack's play must hold for the looks' own moments on its hits (0: none to hold for), so the plate
 * never unmounts one part-way: Toxic's burst at each hit, Sturdy's crack and the backtrack at the first.
 */
export function traitHold(looks: TraitLooks, hits: readonly BurstHit[]): number {
  const ats = landed(hits).map((h) => h.at);
  if (!ats.length) return 0;
  const [first, last] = [Math.min(...ats), Math.max(...ats)];
  return Math.max(
    looks.toxic > 0 ? last + AFTER_HIT.toxic : 0,
    looks.sturdy ? first + AFTER_HIT.sturdy : 0,
    looks.warpInst ? first + AFTER_HIT.backtrack : 0,
  );
}

/** What a body's looks read off its sheet: its Psy Rating, its traits and talents (a statblock's sheet too). */
export type LookSheet = Partial<Pick<Character, 'psyRating' | 'traits' | 'talents'>>;

/** A sheet trait's X: its parameter as a number, 1 when it names none; 0 without the trait. */
function traitX(sheet: LookSheet | null, id: string): number {
  const trait = sheet?.traits?.find((t) => t.traitId === id);
  if (!trait) return 0;
  return Math.max(1, parseInt(trait.parameter ?? '', 10) || 1);
}

const hasTrait = (sheet: LookSheet | null, id: string) => sheet?.traits?.some((t) => t.traitId === id) ?? false;
const hasTalent = (sheet: LookSheet | null, id: string) => sheet?.talents?.some((t) => t.talentId === id) ?? false;

/** The natures off the Target (Incorporeal, Nightmares, Undying) and the sheet (Phase, lift, Warp Instability). */
function naturesOf(target: Target, sheet: LookSheet | null) {
  const lift = hasTrait(sheet, 'flyer') ? 'fly' : hasTrait(sheet, 'hoverer') ? 'hover' : null;
  return {
    incorporeal: target.incorporeal === true,
    phase: hasTrait(sheet, 'phase'),
    lift: lift as TraitLooks['lift'],
    nightmares: target.stuffOfNightmares === true,
    warpInst: hasTrait(sheet, 'warp_instability'),
    undying: target.undying === true,
  };
}

/** The rest of its traits: Natural Armour and Frenzied off the Target, the others off the sheet. */
function marksOf(target: Target, sheet: LookSheet | null) {
  return {
    natArmour: target.naturalArmour || 0,
    sturdy: hasTrait(sheet, 'sturdy'),
    carapace: hasTrait(sheet, 'black_carapace'),
    dnw: hasTrait(sheet, 'deadly_natural_weapons'),
    swift: hasTrait(sheet, 'swift_trait'),
    strange: hasTrait(sheet, 'strange_physiology'),
    frenzied: target.frenzied === true,
    blind: hasTrait(sheet, 'blind'),
  };
}

/** The auras off the Target (Regeneration, Fear, From Beyond) and the sheet (Toxic, its fists, Warp Conduit). */
function aurasOf(target: Target, sheet: LookSheet | null) {
  const toxic = traitX(sheet, 'toxic_trait');
  return {
    regen: target.regeneration || 0,
    toxic,
    toxicFists: toxic > 0 && hasTrait(sheet, 'natural_weapons'),
    fear: target.fearRating || 0,
    conduit: hasTalent(sheet, 'warp_conduit'),
    beyond: target.fromBeyond === true,
  };
}

const clampPsy = (n: number) => Math.min(10, Math.max(0, n));

/** The daemonic treatment's strength: its codex tier's place, MINIMA 1/7 to ULTIMA 1 (user ruling). */
function daemonPower(target: Target, sheet: Character | null): number {
  const tier = classifyThreat(target, sheet).tier;
  return (TIER_ORDER.indexOf(tier) + 1) / TIER_ORDER.length;
}

/**
 * The looks a body shows: its Psy Rating (its own, else its sheet's - as the threat assessment reads it), its force
 * field's rating while up, and the daemonic treatment when it has Daemonic protection or is a daemon by its figure.
 */
export function traitLooks(target: Target, sheet: LookSheet | null, figure: PlateFigureId): TraitLooks {
  const daemonic = target.daemonicProtection > 0 || figure === 'daemon';
  return {
    psy: clampPsy(target.psyRating || sheet?.psyRating || 0),
    field: target.forceField?.active ? target.forceField.protectionRating : 0,
    daemon: daemonic ? daemonPower(target, sheet as Character | null) : 0,
    ...aurasOf(target, sheet),
    ...naturesOf(target, sheet),
    ...marksOf(target, sheet),
  };
}
